"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePublicVenuePageCarousel, useVenuePage } from "../../hooks";

import Autoplay from "embla-carousel-autoplay";
import ClassNames from "embla-carousel-class-names";
import { EmblaCarouselType, EmblaPluginType } from "embla-carousel";
import { ShowWithComedian } from "@punchup/data/legacy";
import { VenueCarouselItem } from "@punchup/data/models";
import VenueImageCarouselItem from "./VenueImageCarouselItem";
import VenueShowCarouselItem from "./VenueShowCarouselItem";
import { cn } from "@punchup/utils/cn";
import { getColorBasedOnContrast } from "@punchup/utils/colorContrast";
import useEmblaCarousel from "embla-carousel-react";
import { useFeatureGate } from "statsig-react";

export interface VenueCarouselTheme {
  backgroundColor: string;
  buttonLabelColor: string;
  buttonBackgroundColor: string;
  placeholderBackgroundColor: string;
}

const defaultTheme: VenueCarouselTheme = {
  backgroundColor: "#FFFFFF",
  buttonLabelColor: "#FFFFFF",
  buttonBackgroundColor: "#FF5E26", // primary orange
  placeholderBackgroundColor: "rgb(31, 31, 31)", // a dark grey color, but not quite black
};

export interface VenueCarouselProps {
  venuePageSlug: string;
  venueLogoImg: string | null;
  shows: ShowWithComedian[];
  theme?: VenueCarouselTheme;
}

export default function VenueCarousel({
  venuePageSlug,
  venueLogoImg,
  shows,
  theme = defaultTheme,
}: VenueCarouselProps) {
  const { venuePage } = useVenuePage({ slug: venuePageSlug });
  const { data: carouselData } = usePublicVenuePageCarousel(venuePage?.id);

  const autoRotateDelay = venuePage?.carousel_auto_rotate_delay ?? null;

  const plugins = useMemo(() => {
    const p: EmblaPluginType[] = [ClassNames()];
    if (autoRotateDelay) {
      p.push(Autoplay({ delay: autoRotateDelay, stopOnInteraction: true }));
    }
    return p;
  }, [autoRotateDelay]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      containScroll: "trimSnaps",
      slidesToScroll: 1,
      skipSnaps: false,
      duration: 25,
    },
    plugins
  );

  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const [canScrollNext, setCanScrollNext] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("select", onSelect);
  }, [emblaApi, onSelect]);

  const scrollToIndex = useCallback(
    (index: number) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(index);
    },
    [emblaApi]
  );

  let items: VenueCarouselItem[] = [];

  if (venuePage?.carousel_mode === "custom" && carouselData !== undefined) {
    // if the venue page is uses a "custom" carousel and the data for the carousel is defined
    items = carouselData.items;
  } else {
    // fallback to the next 5 shows
    items = shows.map((show, index) => ({
      type: "show" as const,
      id: show.id,
      order: index,
      show,
    }));
  }

  if (!items || items.length === 0) {
    return null;
  }

  const lightBorderColor = "#e5e5e5";
  const darkBorderColor = "#404040";

  // determine border color based on background color selected by the venue
  const borderColor =
    theme && theme.backgroundColor
      ? getColorBasedOnContrast(
          theme.backgroundColor,
          lightBorderColor,
          darkBorderColor
        )
      : lightBorderColor;

  return (
    <div className="group flex flex-col items-center gap-y-3 md:gap-y-4 relative">
      <div className="hidden md:block absolute top-1/2 -right-8 -translate-y-1/2 z-30 opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button
          onClick={onNextButtonClick}
          disabled={!canScrollNext}
          className={cn(
            "flex flex-col justify-center items-center h-14 w-14 bg-[rgba(22,22,22,0.9)] text-white rounded-full",
            !canScrollNext && "opacity-50"
          )}
          aria-label="Scroll right"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>
      <div className="hidden md:block absolute top-1/2 -left-8 -translate-y-1/2 z-30 opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button
          onClick={onPrevButtonClick}
          disabled={!canScrollPrev}
          className={cn(
            "flex flex-col justify-center items-center h-14 w-14 bg-[rgba(22,22,22,0.9)] text-white rounded-full",
            !canScrollPrev && "opacity-50"
          )}
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      </div>
      <div
        className={cn(
          "w-full relative overflow-hidden md:rounded-lg border-t border-b md:border"
        )}
        style={{ borderColor }}
        ref={emblaRef}
      >
        <div className="flex flex-row touch-pan-y will-change-transform">
          {items.map((item) => {
            switch (item.type) {
              case "show":
                return (
                  <VenueShowCarouselItem
                    key={item.id}
                    show={item.show}
                    theme={theme}
                    venueLogoImg={venueLogoImg}
                  />
                );
              case "image":
                return (
                  <VenueImageCarouselItem
                    key={item.id}
                    id={item.id}
                    imageUrl={item.image.imageUrl}
                    alt={item.image.altText ?? undefined}
                  />
                );
              case "video":
                // Video support not yet implemented
                return null;
            }
          })}
        </div>
      </div>

      <div className="flex items-center justify-center flex-row gap-x-4">
        {items.map((item, i) => {
          const isActive = i === selectedIndex;
          return (
            <button
              key={item.id}
              onClick={() => scrollToIndex(i)}
              aria-label={`Go to slide ${i + 1} of ${items.length}`}
              aria-current={isActive}
              className="flex flex-col items-center justify-center"
            >
              <span
                className={cn(
                  "h-[10px] rounded-full transition-all ease-in-out duration-500",
                  isActive ? "w-[32px]" : "w-[10px]"
                )}
                style={{
                  backgroundColor:
                    isActive && theme
                      ? theme.buttonBackgroundColor
                      : "rgb(229 229 229)",
                }}
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}
